import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Search, Calendar, Pin, Loader2, AlertCircle } from "lucide-react";
import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useAnnouncements } from "../../../services/announcementService";
import { Input } from "../../../components/ui/input";
import { Card, CardContent } from "../../../components/ui/card";

const Announcements: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: announcementsResponse,
    isLoading,
    error,
  } = useAnnouncements({
    per_page: 12,
    page: currentPage,
    locale: currentLanguage,
    search: searchQuery || undefined,
  });

  const announcements = announcementsResponse?.announcements || [];
  const pagination = announcementsResponse?.pagination;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />
      <main className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-4">
              {t("announcements.title", "Announcements")}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t(
                "announcements.subtitle",
                "Stay updated with our latest community announcements and important updates."
              )}
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8 max-w-2xl mx-auto"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={t(
                  "announcements.search_placeholder",
                  "Search announcements..."
                )}
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 py-6 text-lg rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>
          </motion.div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("announcements.error", "Failed to load announcements")}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Announcements Grid */}
          {!isLoading && !error && (
            <>
              {announcements.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {announcements.map((announcement, index) => (
                    <motion.div
                      key={announcement.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -8, scale: 1.02 }}
                    >
                      <Card
                        className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden h-full cursor-pointer group"
                        onClick={() =>
                          navigate(
                            `/${currentLanguage}/announcements/${announcement.id}`
                          )
                        }
                      >
                        {announcement.is_pinned && (
                          <div className="h-1 bg-gradient-to-r from-red-500 to-pink-500" />
                        )}
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            {announcement.is_pinned && (
                              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <Pin className="w-4 h-4" />
                                <span className="text-xs font-semibold">
                                  {t("announcements.pinned", "Pinned")}
                                </span>
                              </div>
                            )}
                            {announcement.created_at && (
                              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
                                <Calendar className="w-3 h-3" />
                                <span>
                                  {formatDate(announcement.created_at)}
                                </span>
                              </div>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                            {announcement.title}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-4 mb-4">
                            {announcement.body}
                          </p>
                          <button className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                            {t("announcements.readMore", "Read More")}
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="bg-white dark:bg-gray-800">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">
                      {t(
                        "announcements.no_announcements",
                        "No announcements found"
                      )}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Pagination */}
              {pagination && pagination.last_page > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("common.previous", "Previous")}
                  </button>
                  <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {t("common.page", "Page")} {currentPage}{" "}
                    {t("common.of", "of")} {pagination.last_page}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(pagination.last_page, p + 1)
                      )
                    }
                    disabled={currentPage === pagination.last_page}
                    className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("common.next", "Next")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default Announcements;
